import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, setRecoveryToken } from '../stores/authStore';
import { api, type ServerCapabilities } from '../services/api';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setAuth, setLoading } = useAuthStore();
  const [capabilities, setCapabilities] = useState<ServerCapabilities | null>(null);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryTokenInput, setRecoveryTokenInput] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);

  // Fetch server capabilities on mount
  useEffect(() => {
    api.getCapabilities()
      .then(setCapabilities)
      .catch(console.error);
  }, []);

  useEffect(() => {
    // Handle OAuth callback
    const token = searchParams.get('token');
    const userParam = searchParams.get('user');

    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        setAuth(user, token);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Failed to parse user data:', error);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [searchParams, setAuth, setLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGitHubLogin = () => {
    window.location.href = '/api/auth/github';
  };

  const handleLocalLogin = async () => {
    setIsLocalLoading(true);
    setError(null);

    try {
      const response = await api.loginLocal();
      if (response.success) {
        // Pass recoveryToken for session persistence (Issue #164)
        setAuth(response.user, response.token, response.recoveryToken);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to continue locally');
    } finally {
      setIsLocalLoading(false);
    }
  };

  const handleRecoverAccount = async () => {
    if (!recoveryTokenInput.trim()) {
      setError('Please enter your recovery token');
      return;
    }

    setIsRecovering(true);
    setError(null);

    try {
      const response = await api.recoverSession(recoveryTokenInput.trim());
      if (response.success && response.user) {
        // Store the recovery token for future use
        setRecoveryToken(recoveryTokenInput.trim());
        setAuth(response.user, response.token);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired recovery token');
    } finally {
      setIsRecovering(false);
    }
  };

  // Check if local auth is available (requires both localAnalysis enabled AND authMode that supports it)
  const showLocalOption = capabilities?.localAnalysis &&
    (capabilities.authMode === 'local' || capabilities.authMode === 'both');

  // Check if GitHub auth is available
  const showGitHubOption = capabilities &&
    (capabilities.authMode === 'github' || capabilities.authMode === 'both');

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] px-4">
      <div className="w-full max-w-md">
        <div className="bg-background border border-border rounded-lg p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-center mb-6">
            Sign in to Veodiagram
          </h1>

          {/* #1080 v1.0.1: the API client redirects here with
              ?reason=session-expired when it gets back a 401 with
              code: USER_NOT_FOUND (stale JWT cookie referencing a deleted
              user). Show a small banner so the user understands why they
              were bounced out. */}
          {searchParams.get('reason') === 'session-expired' && (
            <div className="mb-4 p-3 bg-amber-100 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-md text-sm text-amber-900 dark:text-amber-200">
              Your previous session is no longer valid. Please sign in again.
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
              {error}
            </div>
          )}

          {showGitHubOption && (
            <>
              <p className="text-muted-foreground text-center mb-6">
                Connect your GitHub account to analyze GitHub repositories
              </p>

              <button
                onClick={handleGitHubLogin}
                className="w-full flex items-center justify-center gap-3 rounded-md bg-[#24292e] px-4 py-3 text-white font-medium hover:bg-[#2f363d] transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
                Continue with GitHub
              </button>
            </>
          )}

          {showLocalOption && showGitHubOption && (
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>
          )}

          {showLocalOption && (
            <>
              {!showGitHubOption && (
                <p className="text-muted-foreground text-center mb-6">
                  Analyze local repositories on your machine
                </p>
              )}

              {capabilities?.localAnalysis && (
                <p className="text-sm text-muted-foreground text-center mb-3">
                  No GitHub account needed for local repository analysis
                </p>
              )}

              <button
                onClick={handleLocalLogin}
                disabled={isLocalLoading}
                className="w-full flex items-center justify-center gap-3 rounded-md border border-border px-4 py-3 font-medium hover:bg-muted transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                {isLocalLoading ? 'Starting...' : 'Continue with Local Repos'}
              </button>
            </>
          )}

          {!capabilities && (
            <div className="text-center py-4">
              <p className="text-muted-foreground">Loading options...</p>
            </div>
          )}

          {/* Recovery Account Section */}
          {showLocalOption && (
            <div className="mt-6 pt-6 border-t border-border">
              {!showRecovery ? (
                <button
                  onClick={() => setShowRecovery(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border border-dashed border-primary/50 rounded-md text-primary hover:bg-primary/5 hover:border-primary transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Have a recovery token? Recover your account
                </button>
              ) : (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground block">
                    Enter your recovery token:
                  </label>
                  <input
                    type="text"
                    value={recoveryTokenInput}
                    onChange={(e) => setRecoveryTokenInput(e.target.value)}
                    placeholder="Paste your recovery token here"
                    className="w-full px-3 py-2 text-sm font-mono bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowRecovery(false);
                        setRecoveryTokenInput('');
                        setError(null);
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRecoverAccount}
                      disabled={isRecovering || !recoveryTokenInput.trim()}
                      className="flex-1 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {isRecovering ? 'Recovering...' : 'Recover Account'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center mt-6">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
